import React, { Fragment } from 'react'
import { StaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import ThemeConsumer from '@/hoc/ThemeConsumer'
import { layouts } from '@/utils/preset'
import { tablet, mobile } from '@/utils/media'
import { mq, rem } from '@/utils/helpers'
import SectionHead from '@/components/app/SectionHead'
import Card from './Card'
import Block from './Block'

const Template = props => {
  const { ...others } = props
  return (
    <StaticQuery
      query={query}
      render={data => (
        <Root {...others}>
          <Container>
            <Head
              sub="REASON"
              main={(
                <Fragment>私たちが選ばれる<br /><strong>4つの理由</strong></Fragment>
              )}
            />
            <Summary>
              <Summary_Inner>
                <Summary_Item>
                  <Card
                    number="1"
                    content={(
                      <Fragment>メーカーとして<br /><strong>自社ECを運用</strong></Fragment>
                    )}
                  />
                </Summary_Item>
                <Summary_Item>
                  <Card
                    number="2"
                    content={(
                      <Fragment>最適化された<br /><strong>デザイン</strong></Fragment>
                    )}
                  />
                </Summary_Item>
                <Summary_Item>
                  <Card
                    number="3"
                    content={(
                      <Fragment><strong>専属ライター</strong><br />在籍</Fragment>
                    )}
                  />
                </Summary_Item>
                <Summary_Item>
                  <Card
                    number="4"
                    content={(
                      <Fragment>納品後の<br /><strong>サポート</strong></Fragment>
                    )}
                  />
                </Summary_Item>
              </Summary_Inner>
            </Summary>
            <Details>
              <Details_Item>
                <Block
                  number={1}
                  heading={(
                    <Fragment><strong>依頼主様の悩み</strong>や<br /><strong>課題</strong>がわかる。</Fragment>
                  )}
                  body={(
                    <Fragment>
                      メーカーとしてアウトドア製品を製造・販売し、自社でECサイトを運用しています。企画・開発から梱包・配送まで、「ものづくり」に関わるすべての業務知識を持っているため、関連業務まで考慮した、公開後に運用しやすいECサイトを制作することができます。<br />
                      また、SEO、広告、SNS、ブログメディアなどさまざまななデジタルマーケティングの手法を日常的に実践し、検証したデータをサイト制作や運用施策に反映させています。
                    </Fragment>
                  )}
                  image={data.image1.childImageSharp.fluid}
                />
              </Details_Item>
              <Details_Item>
                <Block
                  number={2}
                  heading={(
                    <Fragment><strong>購買</strong>につながる<br /><strong>デザイン</strong>を考える。</Fragment>
                  )}
                  body={(
                    <Fragment>
                      ブランドや商品のコンセプト・魅力を理解し、マーケティングに基づいたデザインを作ること。これは、ECサイト制作における私たちのポリシーのひとつです。<br />
                      サイトのかっこよさ・美しさだけではなく、機能面や操作性にも配慮し、お客様の購買意欲と動機を逃さず、購入まで誘導する仕組みもデザインします。
                    </Fragment>
                  )}
                  image={data.image2.childImageSharp.fluid}
                />
              </Details_Item>
              <Details_Item>
                <Block
                  number={3}
                  heading={(
                    <Fragment><strong>文章</strong>を考える<br /><strong>プロ</strong>がいる。</Fragment>
                  )}
                  body={(
                    <Fragment>
                      見た目が優れているだけのECサイトでは、お客様の心をつかむことはできません。売れるECサイトを構築する鍵となるのは「優れたテキスト」です。<br />
                      文字情報は専属のライターが担当し、商品説明・コピーから、ボタンやリンクの文言まで、ユーザビリティを重視してライティングを行います。
                    </Fragment>
                  )}
                  image={data.image3.childImageSharp.fluid}
                />
              </Details_Item>
              <Details_Item>
                <Block
                  number={4}
                  heading={(
                    <Fragment>サイト完成後も<br /><strong>しっかりサポート</strong>。</Fragment>
                  )}
                  body={(
                    <Fragment>
                      ECサイトは「作ってからが勝負」です。依頼主様の売り上げを伸ばし、共に事業を成長させていくことが健全なビジネスであると私たちは考え、行動しています。<br />
                      ご依頼を受けた際は、メーカー及び業界をリサーチしたうえで制作に着手します。納品後も売上増につながるWEB関連施策を提案。運用についても、依頼主様の希望する形でサポートします。<br />
                      なお、弊社には多様な分野の専門家が在籍しています。サイト外の業務領域に関しても、お気軽にご相談ください。
                    </Fragment>
                  )}
                  image={data.image4.childImageSharp.fluid}
                />
              </Details_Item>
            </Details>
          </Container>
        </Root>
      )}
    />
  )
}

export default ThemeConsumer(Template)

const Root = styled.div`
  background-color: ${props => props.theme.gray.a};
  padding: ${rem(56)} ${rem(layouts.defs.pad.base)};
  @media ${mq.and(tablet)} {
    padding: ${rem(60)} ${rem(layouts.defs.pad.tablet)};
  }
  @media ${mq.and(mobile)} {
    padding: ${rem(56)} ${rem(layouts.defs.pad.mobile)} ${rem(56)};
  }
`

const Head = styled(SectionHead)``

const Container = styled.div`
  margin: 0 auto;
`

const Summary = styled.div`
  margin-top: ${rem(40)};
`

const Summary_Inner = styled.ol`
  display: flex;
  flex-wrap: wrap;
  margin-left: ${rem(-12)};
  margin-right: ${rem(-12)};

  @media ${mq.and(mobile)} {
    margin-top: ${rem(-24)};
  }
`

const Summary_Item = styled.li`
  width: 25%;
  padding: 0 ${rem(12)};

  @media ${mq.and(mobile)} {
    margin-top: ${rem(24)};
    width: 50%;
  }
`

const Details = styled.ol`
  margin-top: ${rem(80)};
`

const Details_Item = styled.li`
  margin-top: ${rem(80)};
`

export const query = graphql`
  query ReasonSectionQuery {
    image1: file(relativePath: { eq: "block-1.png" }) {
      childImageSharp {
        fluid(maxWidth: 808, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    image2: file(relativePath: { eq: "block-2.png" }) {
      childImageSharp {
        fluid(maxWidth: 808, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    image3: file(relativePath: { eq: "block-3.png" }) {
      childImageSharp {
        fluid(maxWidth: 808, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    image4: file(relativePath: { eq: "block-4.png" }) {
      childImageSharp {
        fluid(maxWidth: 808, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`
